import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PermissionCode } from "@koclukpanel/authz";
import { izinVarMi } from "./izin";
import { yerelHaftaBasi } from "@koclukpanel/contracts";
import {
  Alan,
  BosDurum,
  Buton,
  cn,
  FiltreKarti,
  Ikon,
  Iskelet,
  Kart,
  Metin,
  SatirEylemi,
  SatirEylemleri,
  Tablo,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  benGetir,
  raporDurumDegistir,
  raporGetir,
  raporGuncelle,
  raporOlustur,
  raporlariGetir,
  type Ben,
  type Rapor,
  type RaporAramasi,
  type RaporDurumu,
  type RaporYorumlari,
} from "./api";
import { PdfIndirDugmesi, RaporEkBolumleri, usePdfIndir } from "./RaporEkBolumleri";

/**
 * VELI RAPORU HAZIRLAMA — F-REP
 *
 * ================================================================
 * BU EKRANIN KURMADIGI SEY: DURUM MAKINESI
 * ================================================================
 * Hangi durumdan hangisine gecilebilecegini SUNUCU soyluyor
 * (`izinliGecisler`). Arayuz kendi tablosunu kursaydi iki tablo er ya
 * da gec ayrisir ve kullanici, sunucunun reddedecegi bir dugmeye
 * basardi. Daha kotusu: arayuz tablosu sunucudan GENIS olsaydi
 * kullanici "sistem izin verdi" sanip yapamayacagi bir isi denerdi.
 *
 * Ayni sebeple "kendi raporunu onaylayamazsin" kurali BURADA
 * uygulanmiyor. Onu sunucu biliyor (hazirlayan uzmanin kimligini
 * arayuz gormuyor bile) ve reddettiginde mesaji aynen gosteriliyor.
 */

const DURUM_ETIKET: Record<RaporDurumu, string> = {
  DRAFT: "Taslak",
  AWAITING_REVIEW: "Kontrol bekliyor",
  REVISION_REQUESTED: "Revizyon istendi",
  APPROVED: "Onaylandı",
  PUBLISHED: "Veliye gönderildi",
  READ_BY_PARENT: "Veli okudu",
};

/** Renk TEK BASINA anlam tasimiyor — her rozette metin de var. */
const DURUM_SINIF: Record<RaporDurumu, string> = {
  DRAFT: "bg-notr-100 text-notr-700 ring-notr-300",
  AWAITING_REVIEW: "bg-uyari-500/10 text-uyari-600 ring-uyari-500/20",
  REVISION_REQUESTED: "bg-hata-500/10 text-hata-700 ring-hata-500/20",
  APPROVED: "bg-marka-50 text-marka-700 ring-marka-600/20",
  PUBLISHED: "bg-basarili-500/10 text-onay-700 ring-basarili-500/20",
  READ_BY_PARENT: "bg-basarili-500/10 text-onay-700 ring-basarili-500/20",
};

type Gorunum = "birincil" | "ikincil" | "sessiz";

/**
 * ================================================================
 * `READ_BY_PARENT` BURADA YOK — KASITLI
 * ================================================================
 * "Veli okudu" bir OLCUM, bir karar degil: veli raporu ACINCA
 * `veli.service.ts` isaretliyor. Uzmana "Okundu isaretle" dugmesi
 * konsaydi kendi raporunun okundugunu ilan edebilirdi.
 *
 * Asil koruma SUNUCUDA: 31.07.2026'da gecis tablosundan da cikarildi
 * (`rapor-kural.ts` `PUBLISHED: []`), cunku dugmeyi cizmemek istegin
 * elle atilmasini engellemiyordu. Burasi yalnizca kullaniciya
 * calismayacak bir dugme gostermemek icin.
 */
const GECIS_DUGMESI: Partial<
  Record<RaporDurumu, { etiket: string; gorunum: Gorunum; izin: PermissionCode }>
> = {
  // ================================================================
  // `izin` SUNUCUDAKI SECIMIN AYNISI
  // ================================================================
  // `rapor.service.ts` onay ve revizyon icin `rapor.veli.onayla`,
  // digerleri icin `rapor.veli.olustur` istiyor. Burada ayni ayrim
  // yapiliyor ki uzman, basamayacagi bir dugmeyi gormesin: izni
  // olmadan basildiginda gelen cevap "Bu kayda erişim yetkiniz yok"
  // ve bu, uzmana kaydin kendisinde bir sorun varmis gibi gelir.
  //
  // Bu bir GUVENLIK onlemi DEGIL — sunucu kontrolu her istekte
  // ayrica yapiyor (CLAUDE.md kural 1). Amac yalnizca kullaniciya
  // kullanamayacagi seyi gostermemek.
  DRAFT: { etiket: "Taslağa döndür", gorunum: "sessiz", izin: "rapor.veli.olustur" },
  AWAITING_REVIEW: { etiket: "Kontrole gönder", gorunum: "birincil", izin: "rapor.veli.olustur" },
  REVISION_REQUESTED: { etiket: "Revizyon iste", gorunum: "ikincil", izin: "rapor.veli.onayla" },
  APPROVED: { etiket: "Onayla", gorunum: "birincil", izin: "rapor.veli.onayla" },
  PUBLISHED: { etiket: "Veliye gönder", gorunum: "birincil", izin: "rapor.veli.olustur" },
};

interface RaporlarProps {
  ogrenciId: string;
  ogrenciAdi: string;
  onGeri: () => void;
  /**
   * F-REP-07a — eksik haftalık rapor satırından gelindiyse o hafta
   * ("YYYY-MM-DD" Pazartesi–Pazar); "Yeni rapor" formu dolu açılır.
   */
  onDoldurulmusDonem?: { bas: string; bit: string } | null;
}

export function Raporlar({ ogrenciId, ogrenciAdi, onGeri, onDoldurulmusDonem }: RaporlarProps) {
  const [acikRapor, setAcikRapor] = useState<string | null>(null);

  if (acikRapor) {
    return (
      <RaporDuzenle raporId={acikRapor} ogrenciId={ogrenciId} onGeri={() => setAcikRapor(null)} />
    );
  }

  return (
    <RaporListesi
      ogrenciId={ogrenciId}
      ogrenciAdi={ogrenciAdi}
      onAc={setAcikRapor}
      onGeri={onGeri}
      onDoldurulmusDonem={onDoldurulmusDonem ?? null}
    />
  );
}

// =====================================================================
// Liste + yeni rapor
// =====================================================================

function RaporListesi({
  ogrenciId,
  ogrenciAdi,
  onAc,
  onGeri,
  onDoldurulmusDonem,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  onAc: (id: string) => void;
  onGeri: () => void;
  onDoldurulmusDonem: { bas: string; bit: string } | null;
}) {
  const qc = useQueryClient();
  const [yeniAcik, setYeniAcik] = useState(onDoldurulmusDonem !== null);
  /*
    Süzgeç İKİ hâlde: kutulara yazılan (`taslak`) ve "Ara" ile UYGULANAN. Her tuşta
    istek atılsaydı tarih kutusu doldurulurken yarım tarih sunucuya giderdi.
    Arama SUNUCUDA (`rapor-arama.ts`) — kapsam kontrolünden sonra, yalnız bu öğrencide.
  */
  const [taslak, setTaslak] = useState<RaporAramasi>({});
  const [suzgec, setSuzgec] = useState<RaporAramasi>({});
  const suzgecVar = Object.values(suzgec).some((v) => (v ?? "").trim().length > 0);
  const pdf = usePdfIndir();

  const { data, isPending, isError, error, refetch } = useQuery<Rapor[]>({
    // Önek ["raporlar", ogrenciId] korunuyor: başka yerlerdeki tazeleme çağrıları süzgeçli listeyi de kapsar.
    queryKey: ["raporlar", ogrenciId, suzgec],
    queryFn: () => raporlariGetir(ogrenciId, suzgec),
  });

  const olustur = useMutation({
    mutationFn: (d: { bas: string; bit: string }) => raporOlustur(ogrenciId, d.bas, d.bit),
    onSuccess: (r) => {
      setYeniAcik(false);
      void qc.invalidateQueries({ queryKey: ["raporlar", ogrenciId] });
      void qc.invalidateQueries({ queryKey: ["eksik-haftalik-raporlar"] });
      // Taslak uretildi; uzmani dogrudan yazma ekranina aliyoruz.
      // Listeye donmek "simdi ne yapacagim" sorusu dogururdu.
      onAc(r.id);
    },
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Raporlar alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        "Listeye Dön" ve "Yeni Rapor" UNİKOÇ'taki gibi BANDIN SAĞINDA (tarama-2 §3.18). Kabuk dışında
        (birim testi) `SayfaBasligiEki` eylemleri olduğu yerde çiziyor — "Yeni rapor" ekranın tek
        giriş yolu, kaybolmuyor.
      */}
      <SayfaBasligiEki
        aciklama="Öğrenci seç, raporları görüntüle; filtrele ve yönet."
        eylemler={
          <>
            <Buton
              gorunum="sessiz"
              onClick={onGeri}
              className="text-mor-700 font-semibold underline"
            >
              ← İş listesi
            </Buton>
            <Buton gorunum="kesikli" onClick={() => setYeniAcik((a) => !a)}>
              {!yeniAcik && <Ikon ad="ekle" className="size-4" />}
              {yeniAcik ? "Vazgeç" : "Yeni rapor"}
            </Buton>
          </>
        }
      />

      {yeniAcik && (
        <YeniRaporFormu
          ogrenciAdi={ogrenciAdi}
          baslangicDonemi={onDoldurulmusDonem}
          calisiyor={olustur.isPending}
          hata={olustur.error instanceof ApiHatasi ? olustur.error : null}
          onGonder={(bas, bit) => olustur.mutate({ bas, bit })}
        />
      )}

      {/*
        UNİKOÇ "Öğrenci ve Filtreler" (tarama-2 §3.18): üst sırada öğrenci · başlangıç · bitiş ·
        degrade arama düğmesi; alt sırada metin arama + kesikli "Temizle". Öğrenci burada SEÇİLMİŞ
        hâlde (seçim öğrenci seçici ekranında); kutuda adı, yanında değiştirme yolu.
      */}
      <FiltreKarti baslik="Öğrenci ve Filtreler" ikon="ogrenci">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-metin-koyu text-sm font-semibold">Öğrenci</span>
            <div className="border-mor-kenar flex min-h-11 items-center gap-2 rounded-xl border px-3">
              <span
                aria-hidden="true"
                className="bg-mor-50 text-mor-700 grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold"
              >
                {ogrenciAdi.charAt(0).toLocaleUpperCase("tr-TR")}
              </span>
              <span className="text-metin-koyu min-w-0 flex-1 truncate text-sm font-semibold">
                {ogrenciAdi}
              </span>
              <button
                type="button"
                onClick={onGeri}
                className="text-mor-700 shrink-0 text-xs font-semibold underline"
              >
                Değiştir
              </button>
            </div>
          </div>
          <Alan
            etiket="Başlangıç"
            type="date"
            value={taslak.bas ?? ""}
            onChange={(e) => setTaslak({ ...taslak, bas: e.target.value })}
          />
          <Alan
            etiket="Bitiş"
            type="date"
            value={taslak.bit ?? ""}
            onChange={(e) => setTaslak({ ...taslak, bit: e.target.value })}
          />
          <Buton onClick={() => setSuzgec(taslak)} className="md:min-w-40">
            <Ikon ad="filtre" className="size-4" />
            Ara
          </Buton>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] md:items-end">
          <Alan
            etiket="Metin arama"
            type="search"
            placeholder="Raporlarda ara…"
            maxLength={100}
            value={taslak.ara ?? ""}
            onChange={(e) => setTaslak({ ...taslak, ara: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSuzgec(taslak);
            }}
          />
          <Buton
            gorunum="kesikli"
            disabled={!suzgecVar && Object.keys(taslak).length === 0}
            onClick={() => {
              setTaslak({});
              setSuzgec({});
            }}
          >
            Temizle
          </Buton>
        </div>
      </FiltreKarti>

      {pdf.error instanceof ApiHatasi && (
        <p role="alert" className="text-hata-700 text-sm">
          {pdf.error.message}
        </p>
      )}

      {data.length === 0 && suzgecVar ? (
        <BosDurum
          baslik="Aramaya uyan rapor yok"
          aciklama="Tarih aralığını genişletin ya da aramayı temizleyin."
        />
      ) : data.length === 0 ? (
        <BosDurum
          baslik="Henüz rapor yok"
          aciklama={`${ogrenciAdi} için ilk dönem raporunu "Yeni rapor" ile hazırlayabilirsiniz. Sayısal bölüm sistem tarafından üretilir.`}
        />
      ) : (
        /*
          UNİKOÇ "Veli Raporları" tablosu: degrade başlık, tarih + durum + işlem sütunu.
          Satır eylemi göz (mor→pembe); düzenle/PDF/sil bizde ayrı eylem değil — rapor
          açılınca editörde (düzenleme) ve durum geçişleriyle yönetiliyor.
        */
        <div className="bg-notr-0 border-mor-kenar rounded-[1.5rem] border p-2 shadow-(--golge-unk-kart) md:p-4">
          <Tablo
            baslikTuru="degrade"
            satirlar={data}
            anahtar={(r) => r.id}
            sutunlar={[
              {
                baslik: "Dönem",
                mobil: true,
                hucre: (r) => (
                  <span className="text-metin-koyu font-semibold">
                    {donemMetni(r.periodStart, r.periodEnd)}
                  </span>
                ),
              },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (r) => (
                  <span
                    className={cn(
                      "rounded-alan inline-block px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                      DURUM_SINIF[r.status],
                    )}
                  >
                    {DURUM_ETIKET[r.status]}
                  </span>
                ),
              },
              { baslik: "Veri kapsamı", hucre: (r) => kapsamMetni(r.dataCoverage) },
              {
                baslik: "İşlemler",
                mobil: true,
                etiketGizli: true,
                hucre: (r) => (
                  <SatirEylemleri>
                    <SatirEylemi
                      tur="gor"
                      etiket={`${donemMetni(r.periodStart, r.periodEnd)} raporunu aç`}
                      onClick={() => onAc(r.id)}
                    />
                    {/*
                      DÜZENLE yalnız veliye GİTMEMİŞ raporda (F-REP-02: gönderilen rapor
                      değişmez). Aynı editörü açar; yazma izni yoksa editör kilidin sebebini yazar.
                    */}
                    {!r.veliyeAcik && (
                      <SatirEylemi
                        tur="duzenle"
                        etiket={`${donemMetni(r.periodStart, r.periodEnd)} raporunu düzenle`}
                        onClick={() => onAc(r.id)}
                      />
                    )}
                    {/*
                      PDF yalnız VELİYE GÖNDERİLMİŞ raporda var (yayında üretiliyor). Taslakta
                      düğme hiç çizilmiyor: basılınca "PDF yok" diyen bir düğme gürültü olurdu.
                      Ad tarih taşımıyor: aynı satırdaki "aç" düğmesinin adıyla karışmasın.
                    */}
                    {r.veliyeAcik && (
                      <SatirEylemi
                        tur="indir"
                        etiket="Raporun PDF'ini indir"
                        disabled={pdf.isPending}
                        onClick={() => pdf.mutate(r.id)}
                      />
                    )}
                  </SatirEylemleri>
                ),
              },
            ]}
          />
          <p className="text-metin-ikincil px-2 pt-3 text-sm">
            {data.length} kayıttan 1–{data.length} arası gösteriliyor
          </p>
        </div>
      )}
    </div>
  );
}

function YeniRaporFormu({
  ogrenciAdi,
  baslangicDonemi = null,
  calisiyor,
  hata,
  onGonder,
}: {
  ogrenciAdi: string;
  baslangicDonemi?: { bas: string; bit: string } | null;
  calisiyor: boolean;
  hata: ApiHatasi | null;
  onGonder: (bas: string, bit: string) => void;
}) {
  const varsayilan = baslangicDonemi ?? sonDortHafta();
  const [bas, setBas] = useState(varsayilan.bas);
  const [bit, setBit] = useState(varsayilan.bit);

  const tersMi = bit < bas;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!tersMi) onGonder(bas, bit);
      }}
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
    >
      <p className="text-notr-600 text-sm">
        {ogrenciAdi} için dönem seçin. Sayısal bölüm bu aralıktan üretilir; sonradan değiştirilemez.
      </p>

      {/*
        F-REP-07a — haftalık rapor = tam Pazartesi–Pazar. Kısayol elle
        tarih seçerken bir gün kaydırıp "aylık rapor" muamelesi gören
        (beklentiyi karşılamayan) dönem üretilmesini önlüyor.
      */}
      <div>
        <Buton
          gorunum="ikincil"
          onClick={() => {
            const h = gecenHafta();
            setBas(h.bas);
            setBit(h.bit);
          }}
        >
          Geçen hafta
        </Buton>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Alan
          etiket="Dönem başlangıcı"
          type="date"
          required
          value={bas}
          onChange={(e) => setBas(e.target.value)}
        />
        <Alan
          etiket="Dönem bitişi"
          type="date"
          required
          value={bit}
          onChange={(e) => setBit(e.target.value)}
        />
      </div>

      {tersMi && (
        <p role="alert" className="text-hata-500 text-sm">
          Dönem bitişi başlangıçtan önce olamaz.
        </p>
      )}

      {hata && (
        <p role="alert" className="text-hata-500 text-sm">
          {hata.message}
        </p>
      )}

      <Buton type="submit" disabled={tersMi} yukleniyor={calisiyor}>
        Taslağı üret
      </Buton>
    </form>
  );
}

// =====================================================================
// Tek rapor — yazma ve durum
// =====================================================================

const YORUM_ALANLARI = [
  {
    anahtar: "generalComment",
    etiket: "Genel değerlendirme",
    sinir: 8000,
    yardim: "Yayınlamadan önce ZORUNLU. Veli raporu bu bölümle açar.",
  },
  { anahtar: "positives", etiket: "Olumlu gelişmeler", sinir: 4000, yardim: undefined },
  { anahtar: "improvements", etiket: "Geliştirilecek alanlar", sinir: 4000, yardim: undefined },
  { anahtar: "nextGoals", etiket: "Gelecek dönem hedefleri", sinir: 4000, yardim: undefined },
  {
    anahtar: "parentAdvice",
    etiket: "Veliye önerimiz",
    sinir: 4000,
    yardim: "Boş bırakılırsa raporda bu başlık HİÇ görünmez.",
  },
] as const;

/**
 * Tek raporun yazma ve durum ekrani.
 *
 * `Onay kuyrugu` da bunu kullaniyor: onaycinin gordugu ekranin,
 * uzmanin gordugu ekrandan farkli olmasi icin bir sebep yok — ayni
 * rapor, ayni kurallar. Ikinci bir ekran yazilsaydi biri
 * guncellendiginde digeri eskirdi.
 */
export function RaporDuzenle({
  raporId,
  ogrenciId,
  onGeri,
  geriEtiketi = "← Raporlar",
}: {
  raporId: string;
  ogrenciId: string;
  onGeri: () => void;
  geriEtiketi?: string;
}) {
  const qc = useQueryClient();
  // Form metni, ona ait sürüm ve metnin yüklendiği hâl TEK nesnede: üçü
  // her zaman birlikte güncellenir. Ayrı tutulsaydı bir tanesi eskide
  // kalır ve onay, ekranda görünmeyen bir metne verilirdi.
  const [form, setForm] = useState<RaporFormu | null>(null);
  // Sunucu 409 RAPOR_DEGISTI dedi: arka plan yeniden getirmesi henüz
  // yetişmemiş olsa da ekran "güncel değil" sayılır.
  const [cakisma, setCakisma] = useState(false);
  const [yenileniyor, setYenileniyor] = useState(false);
  const [yenilemeHatasi, setYenilemeHatasi] = useState(false);
  const [gerekce, setGerekce] = useState("");
  const [gerekceIstenen, setGerekceIstenen] = useState<RaporDurumu | null>(null);
  const [kapsamOnayi, setKapsamOnayi] = useState<RaporDurumu | null>(null);

  const { data, isPending, isError, error } = useQuery<Rapor>({
    queryKey: ["rapor", raporId],
    queryFn: () => raporGetir(raporId),
  });

  // Kabuk bunu zaten cekti; buradaki sorgu onbellekten donuyor.
  const { data: ben } = useQuery<Ben>({ queryKey: ["ben"], queryFn: benGetir });

  // Sunucudan gelen degerler forma BIR KEZ yukleniyor. Her tazelemede
  // yeniden yazilsaydi uzmanin yazdigi metin, arka planda calisan bir
  // yeniden getirme yuzunden kaybolurdu. Tazelenen rapor eskiyse bunu
  // `eskidi` bandi soyluyor; metni yalniz "Güncel hâli yükle" degistirir.
  useEffect(() => {
    if (data && form === null) setForm(formKur(data));
  }, [data, form]);

  function cakismayiIsle(e: unknown): void {
    // F-REP-03 — okunan rapor yazma anında değişmiş: güncel hâli çek.
    if (e instanceof ApiHatasi && e.kod === "RAPOR_DEGISTI") {
      setCakisma(true);
      void qc.invalidateQueries({ queryKey: ["rapor", raporId] });
      void qc.invalidateQueries({ queryKey: ["raporlar", ogrenciId] });
    }
  }

  const kaydet = useMutation({
    mutationFn: (g: { yama: RaporYorumlari; beklenenSurum: string }) =>
      raporGuncelle(raporId, g.yama, g.beklenenSurum),
    onSuccess: (r, g) => {
      // Sunucu durumu TASLAK'a çevirdiyse önbellek de onu gösteriyor;
      // "Kontrole gönder" düğmesi yeniden çıkıyor.
      qc.setQueryData(["rapor", raporId], r);
      // Sürüm yanıttaki rapordan, metinle BİRLİKTE yenileniyor. Kullanıcı
      // istek sürerken yazmaya devam ettiyse yazdığı korunur (yeni sürümün
      // üstüne yazılmış metin olarak).
      setForm((onceki) => formSonrasi(onceki, r, g.yama));
      void qc.invalidateQueries({ queryKey: ["raporlar", ogrenciId] });
    },
    onError: cakismayiIsle,
  });

  const durum = useMutation({
    mutationFn: (d: {
      hedef: RaporDurumu;
      beklenenSurum: string;
      gerekce?: string;
      kapsamOnaylandi?: boolean;
    }) =>
      raporDurumDegistir(raporId, d.hedef, {
        gerekce: d.gerekce,
        kapsamOnaylandi: d.kapsamOnaylandi,
        beklenenSurum: d.beklenenSurum,
      }),
    onSuccess: (r) => {
      qc.setQueryData(["rapor", raporId], r);
      // Durum geçişi metni değiştirmez ama sürümü değiştirir; ikisi yine
      // birlikte yanıttan kuruluyor. İstek sürerken yazılmış (kaydedilmemiş)
      // metin ATILMAZ (bağımsız inceleme B3): yalnız sürüm ve kaynak yenilenir,
      // metin kaydedilmemiş olarak kalır.
      setForm((onceki) => {
        const yeni = formKur(r);
        if (onceki === null) return yeni;
        const yazdi = YORUM_ALANLARI.some(
          (a) => onceki.metin[a.anahtar] !== onceki.kaynak[a.anahtar],
        );
        return yazdi ? { ...yeni, metin: onceki.metin } : yeni;
      });
      void qc.invalidateQueries({ queryKey: ["raporlar", ogrenciId] });
      setGerekceIstenen(null);
      setKapsamOnayi(null);
      setGerekce("");
    },
    onError: cakismayiIsle,
  });

  /**
   * "Güncel hâli yükle" — kullanıcının kaydedilmemiş metni YALNIZ bu
   * düğmeyle atılır. Rapor sunucudan TAZE çekilir (önbellekten değil) ve
   * metin + sürüm birlikte yeniden kurulur.
   */
  async function guncelHaliYukle(): Promise<void> {
    setYenileniyor(true);
    setYenilemeHatasi(false);
    try {
      const taze = await qc.fetchQuery<Rapor>({
        queryKey: ["rapor", raporId],
        queryFn: () => raporGetir(raporId),
        staleTime: 0,
      });
      setForm(formKur(taze));
      setCakisma(false);
      kaydet.reset();
      durum.reset();
      // Rapor değişti: bekleyen onay panelleri eski duruma göre açılmıştı.
      setGerekceIstenen(null);
      setKapsamOnayi(null);
    } catch {
      setYenilemeHatasi(true);
    } finally {
      setYenileniyor(false);
    }
  }

  if (isPending || form === null) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Rapor açılamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={onGeri}>
            Geri dön
          </Buton>
        }
      />
    );
  }

  // Daraltilmis kopya: `gecisIste` bir kapanis ve TypeScript
  // `data`nin o cagrildigi anda hala tanimli oldugunu garanti edemiyor.
  const rapor = data;
  const gorulenSurum = form.surum;

  /**
   * Alanlar iki AYRI sebeple kilitlenebiliyor:
   *
   *   1. F-REP-02 — rapor veliye gitti, artik degismez.
   *   2. Kullanicinin YAZMA izni yok. Onayci (`ACADEMIC_LEAD`) matriste
   *      "Veli raporu | TEAM: RA" — okur ve onaylar, YAZMAZ. Kilitli
   *      olmasaydi koordinator uzmanin metnini duzenleyip "Kaydet"e
   *      basar ve sunucudan 403 alirdi; ustelik degistirdigini sanip
   *      onaylayabilirdi.
   *
   * Ikisi de ENGEL degil kolaylik — sunucu zaten reddediyor. Amac
   * kullaniciya yapamayacagi isi yaptirmaya calistirmamak.
   */
  const yazabilir = ben !== undefined && izinVarMi(ben.izinler, "rapor.veli.olustur");
  const kilitli = data.veliyeAcik || !yazabilir;
  // Kaydedilmemiş mi: metin, YÜKLENDİĞİ hâle göre karşılaştırılıyor; arka
  // planda tazelenen `data`ya göre değil (yoksa kullanıcı hiçbir şey
  // yazmadan "kaydedilmemiş değişiklik" görürdü).
  const degisti = YORUM_ALANLARI.some((a) => form.metin[a.anahtar] !== form.kaynak[a.anahtar]);
  // Ekrandaki metin sunucudaki güncel rapordan farklı bir sürümden: onay
  // bu metne verilemez. Asıl koruma sunucuda; ekran da engelliyor.
  const eskidi = cakisma || data.surum !== form.surum;

  // Uzmanin YAPABILECEGI gecisler: hem durum makinesi izin verecek
  // hem de kullanicinin izni olacak. Suzme sonrasi bos kalirsa
  // "Sonraki adim" bolumu hic cizilmiyor — bos bir baslik "burada bir
  // sey olmaliydi" hissi verirdi.
  const gecisler = data.izinliGecisler.filter((h) => {
    const d = GECIS_DUGMESI[h];
    return d !== undefined && ben !== undefined && d.izin in ben.izinler;
  });

  /*
    "Kontrole gönder" için genel değerlendirme (yorum) KAYITLI olmalı: sunucu yoksa `YORUM_YOK` ile reddeder
    ("Genel değerlendirme yazılmadan rapor ilerletilemez."). Ölçü, formun YÜKLENDİĞİ (kaydedilmiş) hâl:
    yazılıp kaydedilmemiş metin sunucuda yoktur; ona "kaydedin" uyarısı zaten çıkıyor. Sunucu kuralı yine
    son söz; ekran yalnız boşa istek attırmıyor.
  */
  const yorumYok = boslaNull(form.kaynak.generalComment) === null;
  const yorumGerekli = (hedef: RaporDurumu): boolean => hedef === "AWAITING_REVIEW" && yorumYok;
  const kontroleGonderilemez = gecisler.some(yorumGerekli);

  function gecisIste(hedef: RaporDurumu): void {
    // Revizyon isteginde gerekce ZORUNLU (sunucu en az 3 karakter
    // istiyor). Once soruluyor ki kullanici hata mesajiyla degil
    // istekle karsilassin.
    if (hedef === "REVISION_REQUESTED") {
      setGerekceIstenen(hedef);
      return;
    }
    // F-REP-08 — dusuk kapsamda yayinlamak ENGELLI degil, ACIK ONAYA
    // bagli. Engellemek, ogrencinin veri girmedigini velinin
    // ogrenmesini engellemek olurdu.
    if (hedef === "PUBLISHED" && rapor.kapsamUyarisi.onayGerekli) {
      setKapsamOnayi(hedef);
      return;
    }
    durum.mutate({ hedef, beklenenSurum: gorulenSurum });
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        UNİKOÇ "Veli Raporu Detayları": bandın sağında Listeye Dön · durum · PDF İndir; altında
        "Rapor Özeti" kutuları. Düzenle/Sil UNİKOÇ'ta düğme; bizde metin aşağıda doğrudan
        düzenleniyor (kilitliyse sebebi yazıyor) ve rapor silinmiyor (CLAUDE.md kural 7).
      */}
      <SayfaBasligiEki
        aciklama={`Veli raporu — ${donemMetni(data.periodStart, data.periodEnd)}`}
        eylemler={
          <>
            <Buton
              gorunum="sessiz"
              onClick={onGeri}
              className="text-mor-700 font-semibold underline"
            >
              {geriEtiketi}
            </Buton>
            {/* F-REP-04 — PDF yalnız veliye gönderilmiş raporda (UNİKOÇ "PDF İndir"). */}
            {data.veliyeAcik && <PdfIndirDugmesi raporId={raporId} />}
          </>
        }
      />

      <Kart gorunum="unk" ikon="rapor" baslik="Rapor Özeti">
        <dl className="mt-1 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="border-mor-kenar bg-mor-50/40 rounded-2xl border p-3">
            <dt className="text-metin-ikincil text-xs font-semibold">Dönem</dt>
            <dd>
              <h2 className="text-metin-koyu text-sm font-bold">
                {donemMetni(data.periodStart, data.periodEnd)}
              </h2>
            </dd>
          </div>
          <div className="border-mor-kenar bg-mor-50/40 rounded-2xl border p-3">
            <dt className="text-metin-ikincil text-xs font-semibold">Durum</dt>
            <dd className="mt-1">
              <span
                className={cn(
                  "rounded-alan inline-block px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                  DURUM_SINIF[data.status],
                )}
              >
                {DURUM_ETIKET[data.status]}
              </span>
            </dd>
          </div>
          <div className="border-mor-kenar bg-mor-50/40 rounded-2xl border p-3">
            <dt className="text-metin-ikincil text-xs font-semibold">Veri kapsamı</dt>
            <dd className="text-metin-koyu text-sm font-bold">
              {data.dataCoverage === null ? "Ölçülemedi" : `%${Math.round(data.dataCoverage)}`}
            </dd>
          </div>
          <div className="border-mor-kenar bg-mor-50/40 rounded-2xl border p-3">
            <dt className="text-metin-ikincil text-xs font-semibold">Veliye gönderildi</dt>
            <dd className="text-metin-koyu text-sm font-bold">
              {data.sentAt ? new Date(data.sentAt).toLocaleDateString("tr-TR") : "—"}
            </dd>
          </div>
        </dl>
      </Kart>

      {/*
        "Gösterilen metin güncel değil" bandı. Rapor, kullanıcı açtıktan
        sonra değişti (arka plan yeniden getirmesi ya da sunucunun 409'u).
        Metin kendiliğinden DEĞİŞMİYOR — kullanıcının yazdığı onaysız
        atılmaz; yalnız bu düğme yeniler. Bant varken onay düğmeleri
        pasif: yanlış metin onaylanmasın.
      */}
      {eskidi && (
        <div
          role="alert"
          className="rounded-alan bg-uyari-500/15 text-uyari-800 flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
        >
          <span>
            Bu rapor siz açtıktan sonra değişti; gösterilen metin güncel değil.
            {degisti && " Kaydedilmemiş yazdığınız metin, yenileyince atılır."}
            {yenilemeHatasi && " Güncel hâl yüklenemedi, tekrar deneyin."}
          </span>
          <Buton gorunum="ikincil" yukleniyor={yenileniyor} onClick={() => void guncelHaliYukle()}>
            Güncel hâli yükle
          </Buton>
        </div>
      )}

      {/*
        ================================================================
        ÜÇÜNCÜ YAZAR — ONAYLAYAN (F-REP-09)
        ================================================================
        `VeliRaporu.dc.html` bu sayfada dört ayrı yazar tanımlıyor ve
        her birine ayrı bir görünüm veriyor. Üçüncüsü buydu:
        *"ONAYLAYAN YAZIYOR: sarı çizgi, salt okunur."* Çizim hazırdı,
        **veri yoktu** — gerekçe yalnızca denetim kaydına yazılıyordu
        ve uzman hiçbir yoldan göremiyordu. Alan geldi, bağlandı.

        ================================================================
        DURUMA DEĞİL, VERİNİN VARLIĞINA BAKIYOR
        ================================================================
        `status === "REVISION_REQUESTED"` diye çizmek yanlış olurdu:
        gerekçe bir sonraki revizyon isteğine kadar YAŞIYOR, yani
        uzman düzeltip raporu onaya geri gönderdiğinde
        (`AWAITING_REVIEW`) metin duruyor.

        Ve tam o an okunması gereken an: *"istenen buydu, yaptım mı?"*
        Duruma bakan bir koşul, metni tam bitirirken gizlerdi.
      */}
      {data.sonRevizyonGerekcesi && (
        <section className="rounded-kart border-notr-200 border-l-uyari-500 bg-notr-0 shadow-kart flex flex-col gap-1 border border-l-4 p-4">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <h3 className="text-notr-900 text-sm font-semibold">Revizyon gerekçesi</h3>
            {/*
              YAZAN BİLİNMİYORSA AD UYDURULMUYOR. Sunucu `yazan: null`
              gönderiyor (yazan arşivlenmiş olabilir) ve buraya
              "Bilinmeyen kullanıcı" gibi bir yer tutucu yazmak,
              olmayan bir ölçümü varmış gibi göstermek olurdu — bugün
              defalarca düzelttiğimiz sınıfın ta kendisi.
            */}
            <span className="text-notr-600 text-xs">
              {data.sonRevizyonGerekcesi.yazan ?? "yazanı bilinmiyor"}
              {data.sonRevizyonGerekcesi.tarih &&
                ` · ${new Date(data.sonRevizyonGerekcesi.tarih).toLocaleDateString("tr-TR")}`}
            </span>
          </div>
          {/*
            SALT OKUNUR — çizimin kuralı. Uzman burada bir şey
            değiştiremez: bu metin başkasının sözü ve raporun
            geçmişinin parçası.
          */}
          <p className="text-notr-700 text-sm whitespace-pre-line">
            {data.sonRevizyonGerekcesi.metin}
          </p>
        </section>
      )}

      {/*
        F-REP-08 — kapsam uyarisi.

        Sunucunun mesaji bir SORU ("yine de göndermek istiyor
        musunuz?") cunku gonderim karari icin yazildi. Gonderilmis bir
        raporda o soruyu sormak, verilmis bir karari yeniden soruyor
        gibi gorunur ve uzmani "bir sey mi yapmam gerekiyor" diye
        dusundururdu. Karar verilmisse ayni bilgi TESPIT olarak
        yaziliyor.

        KOSUL `kilitli` DEGIL `veliyeAcik`: `kilitli` iki sebeple dogru
        olabiliyor (yayinlandi VEYA yazma izni yok) ve yazma izni
        olmayan onaycıya, HENUZ YAYINLANMAMIS bir rapor icin "veliye
        gösteriliyor" yazilmisti. Canli denemede goruldu.
      */}
      {data.kapsamUyarisi.dusukMu && (
        <p role="status" className="rounded-alan bg-uyari-500/15 text-uyari-800 px-3 py-2 text-sm">
          {data.veliyeAcik
            ? `Bu rapor eksik veriye dayanıyor${
                data.dataCoverage === null
                  ? " (bu dönemde atanmış görev yok)"
                  : ` (veri kapsamı %${Math.round(data.dataCoverage)})`
              }. Veliye bu uyarıyla birlikte gösteriliyor.`
            : data.kapsamUyarisi.mesaj}
        </p>
      )}

      {/* ================================================================
          SAYISAL BOLUM — SISTEM URETTI, DEGISTIRILEMEZ (F-REP-01)
          ================================================================
          Duzenlenebilir olsaydi rapor "olculen" olmaktan cikar, uzmanin
          yazdigi bir metne donerdi. Sistem burada deger yargisi da
          uretmiyor: "iyi gitti" gibi bir cumle olsaydi uzman onu
          okumadan onaylayabilir ve veliye sistemin uydurdugu bir
          degerlendirme giderdi. */}
      {data.draftContent && (
        <Kart gorunum="unk" ikon="analiz" baslik="Sayısal bölüm">
          <p className="text-notr-600 mt-0.5 text-xs">
            Sistem üretti. Değiştirilemez — yorum bölümleri aşağıda.
          </p>
          <pre className="rounded-alan bg-notr-50 text-notr-800 mt-2 p-3 font-sans text-sm whitespace-pre-wrap">
            {data.draftContent}
          </pre>
        </Kart>
      )}

      {/* Sürüm 9a — dönemin görüşme ve deneme bölümleri, veliye gidecek hâliyle. */}
      <RaporEkBolumleri raporId={raporId} />

      <Kart gorunum="unk" ikon="duzenle" baslik="Değerlendirmeniz" className="flex flex-col gap-4">
        {/* Kilidin SEBEBI yaziliyor: "neden yazamıyorum" sorusu
            cevapsiz kalmasin. */}
        {data.veliyeAcik ? (
          <p role="status" className="rounded-alan bg-notr-50 text-notr-600 px-3 py-2 text-sm">
            Bu rapor veliye gönderildi ve artık değiştirilemez. Düzeltme gerekiyorsa yeni rapor
            hazırlayın.
          </p>
        ) : (
          !yazabilir && (
            <p role="status" className="rounded-alan bg-notr-50 text-notr-600 px-3 py-2 text-sm">
              Değerlendirmeyi raporu hazırlayan uzman yazar. Siz onaylar ya da revizyon istersiniz.
            </p>
          )
        )}

        {YORUM_ALANLARI.map((a) => (
          <Metin
            key={a.anahtar}
            etiket={a.etiket}
            yardim={a.yardim}
            maxLength={a.sinir}
            sayacGoster
            disabled={kilitli}
            rows={a.anahtar === "generalComment" ? 6 : 3}
            value={form.metin[a.anahtar] ?? ""}
            onChange={(e) =>
              setForm({ ...form, metin: { ...form.metin, [a.anahtar]: e.target.value } })
            }
          />
        ))}

        {kaydet.error instanceof ApiHatasi && kaydet.error.kod !== "RAPOR_DEGISTI" && (
          <p role="alert" className="text-hata-500 text-sm">
            {kaydet.error.message}
          </p>
        )}

        {kaydet.data?.onayDusuruldu === true && data.status === "DRAFT" && (
          <p
            role="status"
            className="rounded-alan bg-uyari-500/15 text-uyari-800 px-3 py-2 text-sm"
          >
            İçerik değiştiği için rapor taslağa döndü; yeniden onaya gönderin.
          </p>
        )}

        {!kilitli && (
          <Buton
            disabled={!degisti}
            yukleniyor={kaydet.isPending}
            onClick={() =>
              kaydet.mutate({
                // Bos metin `null` gonderiliyor: bos dizge "uzman bos
                // birakti" ile "uzman sildi" arasindaki farki kaybeder
                // ve raporda bos bir baslik olarak gorunurdu.
                yama: yorumYamasi(form.metin),
                beklenenSurum: form.surum,
              })
            }
          >
            {degisti ? "Kaydet" : "Kaydedildi"}
          </Buton>
        )}
      </Kart>

      {/* Veli yorumu — F-REP-06 */}
      {data.parentComment && (
        <Kart gorunum="unk" ikon="sohbet" baslik="Velinin yorumu">
          <p className="text-notr-700 mt-1 text-sm whitespace-pre-line">{data.parentComment}</p>
        </Kart>
      )}

      {/* Durum gecisleri — LISTE SUNUCUDAN GELIYOR */}
      {/*
        Durum makinesi ilerlemeye izin veriyor ama BU kullanicinin
        izni yok. Hicbir sey yazmamak, uzmani "rapor takildi mi"
        sorusuyla birakirdi; oysa yapmasi gereken tek sey beklemek.
      */}
      {gecisler.length === 0 && !kilitli && data.izinliGecisler.length > 0 && (
        <p role="status" className="rounded-alan bg-notr-50 text-notr-600 px-3 py-2 text-sm">
          Rapor kontrol sırasında. Onay yetkisi olan bir yönetici inceleyecek.
        </p>
      )}

      {gecisler.length > 0 && (
        <Kart gorunum="unk" ikon="onay" baslik="Sonraki adım" className="flex flex-col gap-3">
          {degisti && (
            <p role="status" className="text-uyari-600 text-sm">
              Kaydedilmemiş değişiklikleriniz var. Durum değiştirmeden önce kaydedin.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {/*
              Tanimadigi gecisi HIC cizmiyor. Sunucuya yeni bir durum
              eklendiginde ekranin "undefined" yazan bir dugme
              gostermesindense o dugmenin hic olmamasi iyidir; eksik
              dugme fark edilir, bozuk dugme guvenilir gorunur.
            */}
            {gecisler.map((hedef) => {
              const d = GECIS_DUGMESI[hedef];
              if (!d) return null;
              return (
                <Buton
                  key={hedef}
                  gorunum={d.gorunum}
                  disabled={degisti || eskidi || yorumGerekli(hedef)}
                  aria-describedby={yorumGerekli(hedef) ? "yorum-yok-aciklamasi" : undefined}
                  yukleniyor={durum.isPending}
                  onClick={() => gecisIste(hedef)}
                >
                  {d.etiket}
                </Buton>
              );
            })}
          </div>

          {kontroleGonderilemez && (
            <p id="yorum-yok-aciklamasi" role="status" className="text-uyari-800 text-sm">
              Genel değerlendirme yazılmadan rapor ilerletilemez.
            </p>
          )}

          {gerekceIstenen && (
            <div className="rounded-alan bg-notr-50 flex flex-col gap-2 p-3">
              {/*
                Bu söz bir tur boyunca TUTULMUYORDU: gerekçe yalnızca
                denetim kaydına yazılıyor, uzmana hiçbir yoldan
                ulaşmıyordu (dört yol da ölçüldü, dördü de kapalıydı).
                Alan geldi (F-REP-09) ve metin yukarıda, sarı çizgili
                bölümde uzmana gösteriliyor — söz artık doğru.
              */}
              <Metin
                etiket="Revizyon gerekçesi"
                yardim="Zorunlu. Raporu hazırlayan uzman bunu görecek; kayda da geçer."
                maxLength={1000}
                rows={3}
                value={gerekce}
                onChange={(e) => setGerekce(e.target.value)}
              />
              <div className="flex gap-2">
                <Buton
                  disabled={gerekce.trim().length < 3 || eskidi || degisti}
                  yukleniyor={durum.isPending}
                  onClick={() =>
                    durum.mutate({
                      hedef: gerekceIstenen,
                      gerekce: gerekce.trim(),
                      beklenenSurum: form.surum,
                    })
                  }
                >
                  Revizyon iste
                </Buton>
                <Buton gorunum="sessiz" onClick={() => setGerekceIstenen(null)}>
                  Vazgeç
                </Buton>
              </div>
            </div>
          )}

          {kapsamOnayi && (
            <div className="rounded-alan bg-uyari-500/10 flex flex-col gap-2 p-3">
              <p className="text-uyari-600 text-sm">{data.kapsamUyarisi.mesaj}</p>
              <div className="flex gap-2">
                <Buton
                  disabled={eskidi || degisti}
                  yukleniyor={durum.isPending}
                  onClick={() =>
                    durum.mutate({
                      hedef: kapsamOnayi,
                      kapsamOnaylandi: true,
                      beklenenSurum: form.surum,
                    })
                  }
                >
                  Yine de gönder
                </Buton>
                <Buton gorunum="sessiz" onClick={() => setKapsamOnayi(null)}>
                  Vazgeç
                </Buton>
              </div>
            </div>
          )}

          {durum.error instanceof ApiHatasi && durum.error.kod !== "RAPOR_DEGISTI" && (
            <p role="alert" className="text-hata-500 text-sm">
              {durum.error.message}
            </p>
          )}
        </Kart>
      )}
    </div>
  );
}

// =====================================================================
// Yardimcilar
// =====================================================================

/** Form: metin + o metnin sürümü + metnin yüklendiği hâl (kaydedilmemiş değişiklik ölçüsü). */
interface RaporFormu {
  metin: RaporYorumlari;
  surum: string;
  kaynak: RaporYorumlari;
}

function metinKur(r: Rapor): RaporYorumlari {
  return {
    generalComment: r.generalComment ?? "",
    positives: r.positives ?? "",
    improvements: r.improvements ?? "",
    nextGoals: r.nextGoals ?? "",
    parentAdvice: r.parentAdvice ?? "",
  };
}

/** Metin ve sürüm AYNI rapordan kurulur — ikisi hiç ayrı kaynaktan gelmez. */
function formKur(r: Rapor): RaporFormu {
  return { metin: metinKur(r), surum: r.surum, kaynak: metinKur(r) };
}

/** İstekte gidecek yama: boş metin `null`. */
function yorumYamasi(metin: RaporYorumlari): RaporYorumlari {
  return Object.fromEntries(YORUM_ALANLARI.map((a) => [a.anahtar, boslaNull(metin[a.anahtar])]));
}

/**
 * Başarılı kayıttan sonra form: sürüm ve yüklenen hâl yanıttaki rapordan.
 * Metin, kullanıcı istek sürerken değiştirmediyse yanıttan (sunucunun
 * kırptığı hâl); değiştirdiyse kullanıcının yazdığı kalır.
 */
function formSonrasi(onceki: RaporFormu | null, r: Rapor, gonderilen: RaporYorumlari): RaporFormu {
  if (onceki === null) return formKur(r);
  const degismedi = YORUM_ALANLARI.every(
    (a) => boslaNull(onceki.metin[a.anahtar]) === (gonderilen[a.anahtar] ?? null),
  );
  return { metin: degismedi ? metinKur(r) : onceki.metin, surum: r.surum, kaynak: metinKur(r) };
}

function boslaNull(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t.length === 0 ? null : t;
}

function donemMetni(bas: string, bit: string): string {
  const b = new Date(bas);
  const s = new Date(bit);
  return `${b.toLocaleDateString("tr-TR", { day: "numeric", month: "long" })} – ${s.toLocaleDateString(
    "tr-TR",
    { day: "numeric", month: "long", year: "numeric" },
  )}`;
}

function kapsamMetni(kapsam: number | null): string {
  // Olculemeyen kapsam "%0" DEGIL. Sifir kapsam "hic veri yok" demek;
  // olculememis olmak "hic gorev atanmamis" da olabilir.
  if (kapsam === null) return "Veri kapsamı ölçülemedi";
  return `Veri kapsamı %${Math.round(kapsam)}`;
}

/** Yeni rapor icin makul varsayilan: son dort hafta. */
/** Geçen haftanın Pazartesi–Pazar'ı, Türkiye takvimiyle (gece 00–03 arası da doğru). */
export function gecenHafta(simdi: Date = new Date()): { bas: string; bit: string } {
  const pzt = new Date(yerelHaftaBasi(simdi).getTime() - 7 * 86_400_000);
  const pzr = new Date(pzt.getTime() + 6 * 86_400_000);
  return { bas: pzt.toISOString().slice(0, 10), bit: pzr.toISOString().slice(0, 10) };
}

function sonDortHafta(): { bas: string; bit: string } {
  const bugun = new Date();
  const bit = new Date(bugun);
  const bas = new Date(bugun);
  bas.setDate(bas.getDate() - 27);
  const g = (d: Date): string => d.toISOString().slice(0, 10);
  return { bas: g(bas), bit: g(bit) };
}
